import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Clock, Info } from "lucide-react";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import {
  generateTimeSlots,
  combineDateAndTime,
  localToUTC,
  utcToLocal,
  getUserTimezone,
  formatScheduledTime,
  COMMON_TIMEZONES,
} from "@/utils/scheduling";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface TestScheduleSettingsProps {
  isScheduled: boolean;
  scheduledStartTime: Date | null;
  scheduledEndTime: Date | null;
  timezone: string;
  onScheduleChange: (data: {
    isScheduled: boolean;
    scheduledStartTime: Date | null;
    scheduledEndTime: Date | null;
    timezone: string;
  }) => void;
}

export function TestScheduleSettings({
  isScheduled,
  scheduledStartTime,
  scheduledEndTime,
  timezone,
  onScheduleChange,
}: TestScheduleSettingsProps) {
  const timeSlots = generateTimeSlots();
  const userTimezone = getUserTimezone();
  
  const [enabled, setEnabled] = useState(isScheduled);
  const [startDate, setStartDate] = useState<Date | undefined>(
    scheduledStartTime ? utcToLocal(scheduledStartTime, timezone) : undefined
  );
  const [startTime, setStartTime] = useState<string>(
    scheduledStartTime ? format(utcToLocal(scheduledStartTime, timezone), "HH:mm") : "09:00"
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    scheduledEndTime ? utcToLocal(scheduledEndTime, timezone) : undefined
  );
  const [endTime, setEndTime] = useState<string>(
    scheduledEndTime ? format(utcToLocal(scheduledEndTime, timezone), "HH:mm") : "17:00"
  );
  const [selectedTimezone, setSelectedTimezone] = useState(timezone || userTimezone);

  useEffect(() => {
    if (!enabled) {
      onScheduleChange({
        isScheduled: false,
        scheduledStartTime: null,
        scheduledEndTime: null,
        timezone: selectedTimezone,
      });
      return;
    }

    if (startDate && endDate) {
      const localStart = combineDateAndTime(startDate, startTime);
      const localEnd = combineDateAndTime(endDate, endTime);
      
      const utcStart = localToUTC(localStart, selectedTimezone);
      const utcEnd = localToUTC(localEnd, selectedTimezone);

      onScheduleChange({
        isScheduled: true,
        scheduledStartTime: utcStart,
        scheduledEndTime: utcEnd,
        timezone: selectedTimezone,
      });
    }
  }, [enabled, startDate, startTime, endDate, endTime, selectedTimezone]);

  const handleToggle = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  };

  const today = startOfDay(new Date());
  const minEndDate = startDate || today;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-base font-semibold">Schedule Test</Label>
          <p className="text-sm text-muted-foreground">
            Automatically activate and deactivate this test at specific times
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggle} />
      </div>

      {enabled && (
        <>
          <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
            <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> When scheduling is enabled, your test will be <strong>inactive until the scheduled start time</strong>. Candidates cannot access it before then.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {/* Timezone Selection */}
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => isBefore(date, today)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger>
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* End Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => isBefore(date, minEndDate)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Time</Label>
                <Select value={endTime} onValueChange={setEndTime}>
                  <SelectTrigger>
                    <Clock className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Summary */}
            {startDate && endDate && (
              <div className="p-4 rounded-lg bg-muted space-y-1">
                <p className="text-sm font-medium">Schedule Summary</p>
                <p className="text-sm text-muted-foreground">
                  Test will be active from{" "}
                  <span className="font-medium text-foreground">
                    {formatScheduledTime(combineDateAndTime(startDate, startTime), selectedTimezone)}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium text-foreground">
                    {formatScheduledTime(combineDateAndTime(endDate, endTime), selectedTimezone)}
                  </span>
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
